import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

import {
  getChannelLink,
  getReferrals,
  getStreak,
  updateNotifications,
  updateProfile,
  type ReferralsState,
  type StreakState,
} from '@/api/client';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import { useUserStore } from '@/store/userStore';
import OrnateFrame from '@/components/OrnateFrame';
import ShareButton from '@/components/ShareButton';
import DateField from '@/components/DateField';
import TimeField from '@/components/TimeField';
import PageNav from '@/components/PageNav';
import { usePayment } from '@/hooks/usePayment';

export default function Profile() {
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const setUser = useUserStore((s) => s.setUser);

  const { pay, paying, payError } = usePayment('sub_priestess_week');

  const isPriestess = user?.subscription_tier === 'priestess';
  const expiresAt = user?.subscription_expires_at;

  // Notification settings — local state mirrored from the user record.
  // The user record may not have these fields if they haven't visited
  // /profile since the migration; default to ON at 9 AM in that case.
  const [notifEnabled, setNotifEnabled] = useState<boolean>(
    user?.notifications_enabled ?? true,
  );
  const [notifHour, setNotifHour] = useState<number>(user?.notification_hour ?? 9);
  const [notifSaved, setNotifSaved] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  // Sync local state when the user record updates (e.g. after auth).
  useEffect(() => {
    if (user?.notifications_enabled !== undefined) {
      setNotifEnabled(user.notifications_enabled);
    }
    if (user?.notification_hour !== undefined && user.notification_hour !== null) {
      setNotifHour(user.notification_hour);
    }
  }, [user?.notifications_enabled, user?.notification_hour]);

  const saveNotifications = async (enabled: boolean, hour: number) => {
    setNotifSaving(true);
    setNotifSaved(false);
    try {
      await updateNotifications({ enabled, hour });
      if (user) {
        setUser({ ...user, notifications_enabled: enabled, notification_hour: hour });
      }
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 1800);
    } catch {
      // Surface no error — toggling will be retried on next interaction.
    } finally {
      setNotifSaving(false);
    }
  };

  const toggleNotif = () => {
    const next = !notifEnabled;
    setNotifEnabled(next);
    saveNotifications(next, notifHour);
  };

  const changeHour = (h: number) => {
    setNotifHour(h);
    saveNotifications(notifEnabled, h);
  };

  // Streak + full reward catalog for the Achievements section.
  const [streak, setStreak] = useState<StreakState | null>(null);
  // Referrals: count, share link, tier ladder.
  const [refs, setRefs] = useState<ReferralsState | null>(null);
  // Public Telegram channel URL — if backend has TELEGRAM_CHANNEL_ID set.
  const [channelUrl, setChannelUrl] = useState<string | null>(null);
  useEffect(() => {
    getStreak().then(setStreak).catch(() => {});
    getReferrals().then(setRefs).catch(() => {});
    getChannelLink().then((r) => setChannelUrl(r.url)).catch(() => {});
  }, []);

  const openChannel = () => {
    if (!channelUrl) return;
    const tg = window.Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(channelUrl);
    else window.open(channelUrl, '_blank');
  };

  // ---- Editable profile fields ----
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBirth, setEditBirth] = useState('');
  const [editQuestion, setEditQuestion] = useState('');
  // Natal-chart inputs. Stored alongside the main profile fields because
  // the user edits them in the same form. birth_lat/lon/tz_offset come
  // from the place autocomplete and the browser TZ — they're never
  // typed by hand.
  const [editTime, setEditTime] = useState('');
  const [editPlace, setEditPlace] = useState('');
  const [editLat, setEditLat] = useState<number | null>(null);
  const [editLon, setEditLon] = useState<number | null>(null);
  const [editTzOffset, setEditTzOffset] = useState<number | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const beginEdit = () => {
    setEditName(user?.display_name ?? user?.first_name ?? '');
    setEditBirth(user?.birth_date ?? '');
    setEditQuestion(user?.main_question ?? '');
    // Strip seconds if Supabase returned "HH:MM:SS" — the <input type=time>
    // expects "HH:MM" and silently breaks otherwise.
    const t = user?.birth_time ?? '';
    setEditTime(t.length >= 5 ? t.slice(0, 5) : t);
    setEditPlace(user?.birth_place ?? '');
    setEditLat(user?.birth_lat ?? null);
    setEditLon(user?.birth_lon ?? null);
    setEditTzOffset(
      user?.birth_tz_offset ?? -new Date().getTimezoneOffset(),
      // ^ JS returns offset in INVERTED minutes (UTC+3 → -180). Negate to
      //   match our backend convention (UTC+3 → +180).
    );
    setEditError(null);
    setEditSaved(false);
    setEditing(true);
  };

  // Auto-open the edit form when arriving from "Натальная карта → Указать
  // данные рождения". The NatalChart screen passes `?edit=natal` to signal
  // this; we trip beginEdit() once on mount and then strip the param so a
  // refresh (or the back button) doesn't trap the user in edit mode.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('edit') === 'natal' && user) {
      beginEdit();
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const cancelEdit = () => {
    setEditing(false);
    setEditError(null);
  };
  const saveEdit = async () => {
    if (!user) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const payload: {
        display_name?: string; main_question?: string; birth_date?: string;
        birth_time?: string; birth_place?: string;
        birth_lat?: number; birth_lon?: number; birth_tz_offset?: number;
      } = {};
      if (editName.trim() && editName.trim() !== user.display_name)
        payload.display_name = editName.trim();
      if (editQuestion.trim() && editQuestion.trim() !== user.main_question)
        payload.main_question = editQuestion.trim();
      if (editBirth && editBirth !== user.birth_date)
        payload.birth_date = editBirth;
      // Natal-chart fields — only send if changed. We compare against the
      // trimmed/stored value; cross-type compares (null vs undefined vs '')
      // are handled by the ?? '' / ?? null normalisations.
      const storedTime = (user.birth_time ?? '').slice(0, 5);
      if (editTime && editTime !== storedTime)
        payload.birth_time = editTime;
      if (editPlace.trim() && editPlace.trim() !== (user.birth_place ?? ''))
        payload.birth_place = editPlace.trim();
      if (editLat !== null && editLat !== (user.birth_lat ?? null))
        payload.birth_lat = editLat;
      if (editLon !== null && editLon !== (user.birth_lon ?? null))
        payload.birth_lon = editLon;
      if (editTzOffset !== null && editTzOffset !== (user.birth_tz_offset ?? null))
        payload.birth_tz_offset = editTzOffset;

      const updated = await updateProfile(payload);
      setUser({ ...user, ...updated });
      setEditing(false);
      setEditSaved(true);
      setTimeout(() => setEditSaved(false), 2000);
    } catch (e) {
      setEditError((e as Error).message);
    } finally {
      setEditSaving(false);
    }
  };

  const formatExpiry = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  };

  const handleBuySubscription = () => {
    pay(() => {
      // Optimistically update the subscription display — the real update
      // arrives via webhook a moment later. The base for "+7 days" has
      // to match the backend's behaviour (payments.py): if the current
      // subscription is still active, the new period STACKS onto its
      // expiry; otherwise it starts from today. Otherwise an active
      // Priestess who renews sees "no change" for the second or two
      // until the webhook lands, which feels like "I paid for nothing".
      if (user) {
        const now = new Date();
        let base = now;
        if (user.subscription_expires_at) {
          const current = new Date(user.subscription_expires_at);
          if (!isNaN(current.getTime()) && current > now) {
            base = current;
          }
        }
        const newExpiry = new Date(base);
        newExpiry.setDate(newExpiry.getDate() + 7);
        setUser({
          ...user,
          subscription_tier: 'priestess',
          subscription_expires_at: newExpiry.toISOString(),
        });
      }
    });
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-6 text-center font-serif text-2xl text-gold-300">
        ⚙ {t('profile.title')}
      </h1>

      {/* User info */}
      {user && (
        <OrnateFrame className="card-glow mb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center
                              rounded-full border border-gold-500/40 bg-night-900/60
                              font-serif text-2xl text-gold-400">
                {(user.display_name ?? user.first_name ?? '✦').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="truncate font-serif text-lg text-gold-200">
                  {user.display_name ?? user.first_name}
                </p>
                <p className="text-xs text-gold-300/50">ID {user.id}</p>
              </div>
            </div>
            {!editing && (
              <button
                type="button"
                onClick={beginEdit}
                className="shrink-0 rounded-full border border-gold-500/30
                           px-3 py-1 text-[10px] uppercase tracking-widest
                           text-gold-300 transition hover:border-gold-400 hover:text-gold-100"
              >
                ✎ {t('profile.edit')}
              </button>
            )}
          </div>

          {/* Read mode */}
          {!editing && (
            <div className="mt-4 space-y-3 border-t border-gold-500/20 pt-4">
              {user.birth_date && (
                <Row label={t('profile.birth_date')} value={user.birth_date} />
              )}
              {user.birth_time && (
                <Row label={t('profile.birth_time')} value={user.birth_time.slice(0, 5)} />
              )}
              {user.birth_place && (
                <Row label={t('profile.birth_place')} value={user.birth_place} />
              )}
              {user.main_question && (
                <Row label={t('profile.main_question')} value={`«${user.main_question}»`} />
              )}
              {editSaved && (
                <p className="text-center text-xs text-gold-400/80">
                  ✓ {t('profile.saved')}
                </p>
              )}
            </div>
          )}

          {/* Edit mode */}
          {editing && (
            <div className="mt-4 space-y-3 border-t border-gold-500/20 pt-4">
              <EditField
                label={t('profile.name_field')}
                value={editName}
                onChange={setEditName}
                maxLength={64}
              />
              <label className="block">
                <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('profile.birth_date')}
                </span>
                <DateField value={editBirth} onChange={setEditBirth} />
              </label>
              {/* Heads-up: today's card is sealed regardless of edits. */}
              {editBirth && editBirth !== user.birth_date && (
                <p className="rounded-lg border border-gold-500/25 bg-night-900/40
                              px-3 py-2 text-[11px] italic leading-snug text-gold-300/70">
                  ☽ {t('profile.birth_change_notice')}
                </p>
              )}
              {/* Natal-chart inputs — optional. Hint nudges users who haven't
                  filled them in yet. */}
              <EditField
                label={t('profile.birth_time')}
                value={editTime}
                onChange={setEditTime}
                type="time"
              />
              <PlaceAutocomplete
                label={t('profile.birth_place')}
                value={editPlace}
                onChange={(place: string, lat: number | null, lon: number | null) => {
                  setEditPlace(place);
                  if (lat !== null) setEditLat(lat);
                  if (lon !== null) setEditLon(lon);
                }}
              />
              {(!user.birth_time || !user.birth_place) && (
                <p className="rounded-lg border border-gold-500/25 bg-night-900/40
                              px-3 py-2 text-[11px] italic leading-snug text-gold-300/70">
                  ✦ {t('profile.natal_hint')}
                </p>
              )}
              <EditField
                label={t('profile.main_question')}
                value={editQuestion}
                onChange={setEditQuestion}
                maxLength={280}
                multiline
              />

              {editError && (
                <p className="text-center text-xs text-rose-400">{editError}</p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={editSaving}
                  className="flex-1 rounded-lg border border-gold-500/30 py-2
                             text-sm text-gold-300/80 transition hover:border-gold-400
                             disabled:opacity-40"
                >
                  {t('profile.cancel')}
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={editSaving}
                  className="flex-1 rounded-lg border border-gold-500
                             bg-gold-500/20 py-2 text-sm text-gold-100
                             transition hover:bg-gold-500/30 disabled:opacity-40"
                >
                  {editSaving ? t('profile.saving') : t('profile.save')}
                </button>
              </div>
            </div>
          )}
        </OrnateFrame>
      )}

      {/* Channel CTA — placed right after the user card so it's
          impossible to miss. Animated pulse on the icon draws the eye. */}
      {channelUrl && (
        <button
          type="button"
          onClick={openChannel}
          className="mb-4 flex w-full items-center justify-between rounded-2xl
                     border-2 border-gold-500/50 bg-gradient-to-br
                     from-gold-500/20 to-gold-500/5 px-4 py-3.5
                     shadow-[0_0_20px_rgba(232,195,86,0.15)]
                     transition active:scale-[0.99] hover:border-gold-400"
        >
          <span className="flex items-center gap-3">
            <span className="text-2xl animate-pulse">📢</span>
            <span className="text-left">
              <span className="block font-serif text-base text-gold-100">
                {t('profile.channel_title')}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-gold-400">
                {t('profile.channel_subtitle')}
              </span>
            </span>
          </span>
          <span className="text-xl text-gold-300">→</span>
        </button>
      )}

      {/* Subscription */}
      <OrnateFrame className="card-glow mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm uppercase tracking-widest text-gold-300/60">
            {t('profile.subscription')}
          </span>
          <span className={`rounded-full px-3 py-0.5 text-xs font-semibold
            ${isPriestess
              ? 'bg-gold-500/20 text-gold-300 border border-gold-500/40'
              : 'bg-night-700 text-gold-300/50'}`}>
            {isPriestess ? t('profile.plan_priestess') : t('profile.plan_free')}
          </span>
        </div>

        {isPriestess && expiresAt && (
          <>
            <p className="mt-2 text-xs text-gold-300/50">
              {t('profile.expires', { date: formatExpiry(expiresAt) })}
            </p>
            {/* Renew button — the missing piece. Earlier this UI only
                showed the expiry date with no way to act on it, while the
                expiry-nudge notifications kept telling users to "продли
                в настройках". Now they actually can — backend stacks the
                new period on top of the current expiry so no days are
                lost by renewing early. */}
            <button
              className="btn-primary mt-3 w-full disabled:opacity-40"
              onClick={handleBuySubscription}
              disabled={paying}
            >
              {paying
                ? t('payment.opening')
                : t('profile.renew_weekly', { stars: 150 })}
            </button>
            <p className="mt-2 text-center text-[11px] italic text-gold-300/60">
              {t('profile.renew_note')}
            </p>
            {payError && (
              <p className="mt-2 text-center text-xs text-rose-400">{payError}</p>
            )}
          </>
        )}
      </OrnateFrame>

      {/* Notifications */}
      <OrnateFrame className="card-glow mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm uppercase tracking-widest text-gold-300/60">
              {t('profile.notifications_title')}
            </p>
            <p className="mt-1 text-xs italic text-gold-300/50">
              {t('profile.notifications_blurb')}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleNotif}
            disabled={notifSaving}
            aria-pressed={notifEnabled}
            className={`relative h-7 w-12 shrink-0 rounded-full border transition
              ${notifEnabled
                ? 'border-gold-500 bg-gold-500/40'
                : 'border-gold-500/30 bg-night-700'}`}
          >
            <span
              className={`absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full
                          bg-gold-300 shadow transition-[left] duration-200
                          ${notifEnabled ? 'left-[calc(100%-1.375rem)]' : 'left-0.5'}`}
            />
          </button>
        </div>

        {notifEnabled && (
          <div className="mt-4 border-t border-gold-500/20 pt-4">
            <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/50">
              {t('profile.notifications_time')}
            </p>
            <div className="grid grid-cols-6 gap-1.5">
              {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => changeHour(h)}
                  disabled={notifSaving}
                  className={`rounded-md py-1.5 text-xs transition
                    ${notifHour === h
                      ? 'bg-gold-500/30 text-gold-200 ring-1 ring-gold-500/60'
                      : 'bg-night-800/60 text-gold-300/60 hover:bg-night-700/80'}`}
                >
                  {h}:00
                </button>
              ))}
            </div>
          </div>
        )}

        {notifSaved && (
          <p className="mt-3 text-center text-xs text-gold-400/80">
            ✓ {t('profile.notifications_saved')}
          </p>
        )}
      </OrnateFrame>

      {/* Referrals */}
      {refs && (
        <OrnateFrame className="card-glow mb-4 border-gold-500/50">
          <p className="mb-1 text-center text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
            ✦ ✦ ✦
          </p>
          <h2 className="mb-1 text-center font-serif text-xl text-gold-200">
            {t('referrals.title')}
          </h2>
          <p className="mb-4 text-center text-xs italic leading-relaxed text-gold-300/70">
            {t('referrals.subtitle')}
          </p>

          {/* Count + next tier hint */}
          <div className="mb-4 rounded-xl border border-gold-500/20 bg-night-900/40 px-3 py-3 text-center">
            <p className="font-serif text-lg text-gold-200">
              {refs.count === 0
                ? t('referrals.no_referrals')
                : t('referrals.count', { count: refs.count })}
            </p>
            {refs.next_tier && (
              <p className="mt-1 text-[11px] uppercase tracking-widest text-gold-500/70">
                {refs.next_tier.icon} {refs.next_tier.title} —{' '}
                {t('referrals.next_tier_in', {
                  count: refs.next_tier.day - refs.count,
                })}
              </p>
            )}
          </div>

          <ShareButton
            shareMessage={t('referrals.share_message')}
            shareLink={refs.share_link}
            label={t('referrals.share_button')}
            className="mb-4"
          />

          {/* Tier ladder */}
          <ul className="mt-2 space-y-2">
            {refs.all_tiers.map((tier) => {
              const unlocked = refs.count >= tier.day;
              return (
                <li
                  key={tier.id}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 transition
                    ${unlocked
                      ? 'border-gold-500/40 bg-gold-500/10'
                      : 'border-gold-500/15 bg-night-900/30'}`}
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center
                                   rounded-full text-xl
                                   ${unlocked ? 'bg-gold-500/20' : 'bg-night-800/60 grayscale opacity-40'}`}>
                    {tier.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className={`font-serif text-sm leading-tight
                        ${unlocked ? 'text-gold-200' : 'text-gold-300/50'}`}>
                        {tier.title}
                      </p>
                      <span className={`shrink-0 text-[10px] uppercase tracking-widest
                        ${unlocked ? 'text-gold-400/80' : 'text-gold-500/40'}`}>
                        {t('referrals.tier_friends', { count: tier.day })}
                      </span>
                    </div>
                    <p className={`mt-0.5 text-xs italic leading-snug
                      ${unlocked ? 'text-gold-300/70' : 'text-gold-300/40'}`}>
                      {tier.blurb}
                    </p>
                    {unlocked && (
                      <p className="mt-1 text-[10px] uppercase tracking-widest text-gold-400/70">
                        ✓ {t('referrals.unlocked')}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </OrnateFrame>
      )}

      {/* Achievements & rewards gallery */}
      {streak && streak.all_rewards && streak.all_rewards.length > 0 && (
        <OrnateFrame className="card-glow mb-4">
          <p className="text-sm uppercase tracking-widest text-gold-300/60">
            {t('streak.achievements_title')}
          </p>
          <p className="mt-1 mb-4 text-xs italic text-gold-300/50">
            {t('streak.achievements_subtitle')}
          </p>

          {/* Streak summary line */}
          <div className="mb-4 flex items-center justify-between rounded-xl
                          border border-gold-500/20 bg-night-900/40 px-3 py-2">
            <span className="flex items-baseline gap-2">
              <span className="text-xl">🔥</span>
              <span className="font-serif text-base text-gold-200">
                {t('streak.days', { count: streak.current })}
              </span>
            </span>
            <span className="text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('streak.longest', { count: streak.longest })}
            </span>
          </div>

          {/* Tier list */}
          <ul className="space-y-2">
            {streak.all_rewards.map((reward) => {
              const day = reward.day ?? 0;
              const unlocked = streak.current >= day;
              return (
                <li
                  key={reward.id}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 transition
                    ${unlocked
                      ? 'border-gold-500/40 bg-gold-500/10'
                      : 'border-gold-500/15 bg-night-900/30'}`}
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center
                                   rounded-full text-2xl
                                   ${unlocked
                                     ? 'bg-gold-500/20'
                                     : 'bg-night-800/60 grayscale opacity-40'}`}>
                    {reward.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className={`font-serif text-sm leading-tight
                        ${unlocked ? 'text-gold-200' : 'text-gold-300/50'}`}>
                        {reward.title}
                      </p>
                      <span className={`shrink-0 text-[10px] uppercase tracking-widest
                        ${unlocked ? 'text-gold-400/80' : 'text-gold-500/40'}`}>
                        {t('streak.tier_day', { count: day })}
                      </span>
                    </div>
                    <p className={`mt-0.5 text-xs italic leading-snug
                      ${unlocked ? 'text-gold-300/70' : 'text-gold-300/40'}`}>
                      {reward.blurb}
                    </p>
                    {unlocked && (
                      <p className="mt-1 text-[10px] uppercase tracking-widest text-gold-400/70">
                        ✓ {t('streak.tier_unlocked')}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </OrnateFrame>
      )}

      {/* Upgrade block — shown for free users */}
      {!isPriestess && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <OrnateFrame className="card-glow border-gold-500/50">
            <div className="mb-1 text-center font-serif-sc text-[11px] uppercase
                            tracking-[0.2em] text-gold-500/70">
              ✦ ✦ ✦
            </div>
            <h2 className="mb-1 text-center font-serif text-xl text-gold-300">
              {t('profile.upgrade_title')}
            </h2>
            <p className="mb-4 text-center text-sm italic text-gold-300/60">
              {t('profile.upgrade_blurb')}
            </p>

            <ul className="mb-5 space-y-2 text-sm text-gold-200/80">
              {[
                t('spread.celtic_cross'),
                t('spread.love'),
                t('subscription.weekly_blurb'),
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-0.5 text-gold-500">✦</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button
              className="btn-primary w-full disabled:opacity-40"
              onClick={handleBuySubscription}
              disabled={paying}
            >
              {paying
                ? t('payment.opening')
                : t('profile.buy_weekly', { stars: 150 })}
            </button>

            {payError && (
              <p className="mt-2 text-center text-xs text-rose-400">{payError}</p>
            )}
          </OrnateFrame>
        </motion.div>
      )}

      <PageNav />
    </main>
  );
}


function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gold-500/50">{label}</div>
      <div className="mt-0.5 font-serif italic text-gold-200">{value}</div>
    </div>
  );
}

function EditField({
  label, value, onChange, maxLength, type = 'text', multiline = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  type?: 'text' | 'date' | 'time';
  multiline?: boolean;
}) {
  // For time/date we route through the in-app TimeField/DateField widgets
  // — they sidestep the Android native picker, which (1) uses the system
  // locale instead of the app locale, and (2) overflows the dialog on
  // narrow phones (the "Установить" CTA gets clipped on common 360-dp
  // devices). Typing-first input is also faster than spinning a clock
  // face when the user already knows their birth time.
  if (!multiline && type === 'time') {
    return (
      <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
          {label}
        </span>
        <TimeField value={value} onChange={onChange} />
      </label>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
        {label}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          rows={3}
          className="w-full resize-none rounded-lg border border-gold-500/30
                     bg-night-900/60 px-3 py-2 font-serif text-base text-gold-100
                     outline-none transition focus:border-gold-400"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          className="w-full rounded-lg border border-gold-500/30
                     bg-night-900/60 px-3 py-2 font-serif text-base text-gold-100
                     outline-none transition focus:border-gold-400"
        />
      )}
    </label>
  );
}


